import { ThreeEvent } from '@react-three/fiber';
import { GridObjectType, GridSpaceType, LevelType } from 'data/types';
import { useEquipmentContextMenu } from 'hooks/use-equipment-context-menu';
import { useEquipmentDirectDrag } from 'hooks/use-equipment-direct-drag';
import { useCallback, useMemo, useRef } from 'react';
import { getLevelIndexForSpace } from 'utils/3d';
import { getEffectiveFootprintPolygons } from 'utils/footprint-polygons';
import { getGridSpacesForLevel } from 'utils/grid';
import { TransformControlsInteractionState } from 'utils/transform-controls';
import AirHandler3D from './air-handler-3d';
import Condenser3D from './condenser-3d';
import ElectricalPanel3D from './electrical-panel-3d';
import EquipmentGizmo3D, { EquipmentDragOverride } from './equipment-gizmo-3d';

type Equipment3DProps = {
  object: GridObjectType;
  spaces: GridSpaceType[];
  structure: LevelType[];
  levelIndex: number;
  yOffset: number;
  opacity: number;
  configurationId?: string;
  readOnly?: boolean;
  selected?: boolean;
  onSelect?: (objectId: string) => void;
  onDragOverrideChange?: (override: EquipmentDragOverride | null) => void;
};

export default function Equipment3D({
  object,
  spaces,
  structure,
  levelIndex,
  yOffset,
  opacity,
  configurationId,
  readOnly = false,
  selected = false,
  onSelect,
  onDragOverrideChange
}: Equipment3DProps) {
  const gridSpaces = useMemo(
    () => getGridSpacesForLevel(structure, levelIndex),
    [structure, levelIndex]
  );

  const levelSpaces = useMemo(
    () =>
      spaces.filter(
        (space) => getLevelIndexForSpace(space.id, structure) === levelIndex
      ),
    [spaces, structure, levelIndex]
  );

  const footprints = useMemo(
    () => getEffectiveFootprintPolygons(structure[levelIndex]),
    [structure, levelIndex]
  );

  const { openEquipmentContextMenu } = useEquipmentContextMenu({
    object,
    configurationId,
    gridSpaces,
    structure
  });

  const menuEnabled =
    !readOnly &&
    !!configurationId &&
    (object.type === 'condenser' || object.type === 'air-handler');

  const movable =
    menuEnabled && opacity >= 1 && !!onSelect && !!onDragOverrideChange;

  const gizmoInteractionRef = useRef<TransformControlsInteractionState>({
    hovered: false,
    dragging: false
  });

  const directDragHandlers = useEquipmentDirectDrag({
    object,
    spaces: levelSpaces,
    footprints,
    structure,
    levelIndex,
    yOffset,
    configurationId,
    gridSpaces,
    isBlocked: () =>
      gizmoInteractionRef.current.hovered ||
      gizmoInteractionRef.current.dragging,
    onDragOverrideChange
  });

  const handleRightClick = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      event.stopPropagation();
      openEquipmentContextMenu(
        event.nativeEvent.clientX,
        event.nativeEvent.clientY
      );
    },
    [openEquipmentContextMenu]
  );

  const handleClick = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      if (event.delta > 5) return;
      event.stopPropagation();
      onSelect?.(object.id);
    },
    [onSelect, object.id]
  );

  const gizmo = movable && selected && configurationId && (
    <EquipmentGizmo3D
      object={object}
      spaces={levelSpaces}
      footprints={footprints}
      structure={structure}
      levelIndex={levelIndex}
      yOffset={yOffset}
      configurationId={configurationId}
      gridSpaces={gridSpaces}
      onDragOverrideChange={onDragOverrideChange!}
      onInteractionChange={(state) => {
        gizmoInteractionRef.current = state;
      }}
    />
  );

  if (object.type === 'condenser') {
    return (
      <>
        <Condenser3D
          object={object}
          spaces={levelSpaces}
          footprints={footprints}
          yOffset={yOffset}
          opacity={opacity}
          selected={movable && selected}
          onClick={movable ? handleClick : undefined}
          onRightClick={menuEnabled ? handleRightClick : undefined}
          dragHandlers={movable ? directDragHandlers : undefined}
        />
        {gizmo}
      </>
    );
  }
  if (object.type === 'electrical-panel') {
    return (
      <ElectricalPanel3D
        object={object}
        spaces={levelSpaces}
        yOffset={yOffset}
        opacity={opacity}
      />
    );
  }
  if (object.type === 'air-handler') {
    return (
      <>
        <AirHandler3D
          object={object}
          spaces={levelSpaces}
          yOffset={yOffset}
          opacity={opacity}
          selected={movable && selected}
          onClick={movable ? handleClick : undefined}
          onRightClick={menuEnabled ? handleRightClick : undefined}
          dragHandlers={movable ? directDragHandlers : undefined}
        />
        {gizmo}
      </>
    );
  }
  return null;
}
